import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ReactMarkdown from 'react-markdown';
import { MarkdownEditor } from '../ui/markdown-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { useTimezone } from '../../hooks/useTimezone';
import { goalsApi, initiativesApi, teamApi } from '../../services/api';
import { toast } from 'sonner';
import { UnifiedCoPilot } from '../copilot';
import { ActiveKPICard } from './ActiveKPICard';
import { NotesPanel, AttachmentsPanel } from '../shared';
import { 
  Target, AlertTriangle, User, Calendar, Search,
  Loader2, Save, Lock,
  Trash2, Plus, X, Rocket,
  TrendingUp, TrendingDown, Minus, ExternalLink,
  ChevronDown, ChevronRight, Pencil, DollarSign, Percent, Clock, Hash,
  Layers, FileText, Settings, Paperclip, MessageSquare
} from 'lucide-react';

// Status options for goals
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'active', label: 'Active', color: 'bg-blue-500' },
  { value: 'achieved', label: 'Achieved', color: 'bg-green-500' },
  { value: 'abandoned', label: 'Abandoned', color: 'bg-red-500' },
];

// Metric format options
const METRIC_FORMAT_OPTIONS = [
  { value: 'number', label: 'Number', icon: Hash, example: '1,234' },
  { value: 'currency', label: 'Currency ($)', icon: DollarSign, example: '$1,234' },
  { value: 'percent', label: 'Percentage (%)', icon: Percent, example: '45%' },
  { value: 'time', label: 'Time', icon: Clock, example: '2h 30m' },
];

// Direction options (Outcome-Driven Model)
const DIRECTION_OPTIONS = [
  { value: 'higher_is_better', label: 'Higher is Better', icon: TrendingUp },
  { value: 'lower_is_better', label: 'Lower is Better', icon: TrendingDown },
];

// Measurement interval options (Outcome-Driven Model)
const INTERVAL_OPTIONS = [
  { value: 'weekly', label: 'Weekly', days: 7 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'quarterly', label: 'Quarterly', days: 90 },
];

// Format value based on metric format
const formatMetricValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    case 'time':
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const mins = Math.round(num % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${num}m`;
    default:
      return num.toLocaleString();
  }
};

// Generate unique KPI ID
const generateKPIId = () => `kpi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// KPI Status Settings Modal
const KPISettingsModal = ({ isOpen, onClose, goalId, currentSettings, onSave }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [settings, setSettings] = useState({
    min_data_points: currentSettings?.min_data_points ?? 2,
    projection_tolerance_pct: currentSettings?.projection_tolerance_pct ?? 10,
    aggregate_method: currentSettings?.aggregate_method ?? 'worst_case'
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (isOpen && currentSettings) {
      setSettings({
        min_data_points: currentSettings.min_data_points ?? 2,
        projection_tolerance_pct: currentSettings.projection_tolerance_pct ?? 10,
        aggregate_method: currentSettings.aggregate_method ?? 'worst_case'
      });
    }
  }, [isOpen, currentSettings]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await goalsApi.updateKpiStatusSettings(goalId, settings);
      if (response.data?.success) {
        toast.success('KPI settings updated');
        onSave(response.data);
        onClose();
      }
    } catch (err) {
      console.error('Failed to update settings:', err);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-xl shadow-xl max-w-xl w-full mx-4", colors.bgCard)}>
        <div className="flex items-center gap-3 mb-6">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
            <Settings className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>KPI Status Settings</h3>
            <p className={cn("text-sm", colors.textMuted)}>Configure how KPI status is calculated</p>
          </div>
        </div>
        
        <div className="space-y-5">
          {/* Min Data Points */}
          <div>
            <Label className={colors.textSecondary}>Minimum Data Points for Trend</Label>
            <p className={cn("text-xs mb-2", colors.textMuted)}>
              How many data points are needed before calculating on/off track status
            </p>
            <Select 
              value={settings.min_data_points.toString()} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, min_data_points: parseInt(v) }))}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="1">1 point (immediate)</SelectItem>
                <SelectItem value="2">2 points (default)</SelectItem>
                <SelectItem value="3">3 points</SelectItem>
                <SelectItem value="4">4 points</SelectItem>
                <SelectItem value="5">5 points (conservative)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Projection Tolerance */}
          <div>
            <Label className={colors.textSecondary}>Projection Tolerance (%)</Label>
            <p className={cn("text-xs mb-2", colors.textMuted)}>
              How close to target the projection needs to be for &ldquo;On Track&rdquo; status (±%)
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="0"
                max="50"
                value={settings.projection_tolerance_pct}
                onChange={(e) => setSettings(prev => ({ ...prev, projection_tolerance_pct: parseFloat(e.target.value) || 0 }))}
                className={cn("w-24", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
              <span className={colors.textMuted}>%</span>
              <span className={cn("text-xs", colors.textMuted)}>
                (e.g., 10% means projected value within ±10% of target = On Track)
              </span>
            </div>
          </div>
          
          {/* Aggregate Method */}
          <div>
            <Label className={colors.textSecondary}>Aggregate Status Method</Label>
            <p className={cn("text-xs mb-2", colors.textMuted)}>
              How to combine individual KPI statuses into overall goal status
            </p>
            <Select 
              value={settings.aggregate_method} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, aggregate_method: v }))}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="worst_case">
                  <div>
                    <span className="font-medium">Worst Case</span>
                    <span className="text-xs text-gray-500 ml-2">Any KPI off track = Goal off track</span>
                  </div>
                </SelectItem>
                <SelectItem value="majority">
                  <div>
                    <span className="font-medium">Majority</span>
                    <span className="text-xs text-gray-500 ml-2">Based on most common KPI status</span>
                  </div>
                </SelectItem>
                <SelectItem value="optimistic">
                  <div>
                    <span className="font-medium">Optimistic</span>
                    <span className="text-xs text-gray-500 ml-2">33% of KPIs on track = On Track</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Settings className="w-4 h-4 mr-1" />}
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

// Activation Validation Modal - Shows requirements before activating a goal
const ActivationValidationModal = ({ isOpen, onClose, validationErrors }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-xl shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Cannot Activate Goal</h3>
            <p className={cn("text-sm", colors.textMuted)}>Please resolve the following issues</p>
          </div>
        </div>
        
        <div className="space-y-3 mb-6">
          {validationErrors.map((error, index) => (
            <div 
              key={index}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border",
                "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
              )}
            >
              <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{error.title}</p>
                <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">{error.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-end">
          <Button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white">
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, isDeleting }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Delete Goal</h3>
        </div>
        <p className={cn("text-sm mb-2", colors.textSecondary)}>
          Are you sure you want to delete this goal?
        </p>
        <p className={cn("text-sm font-medium mb-4 p-2 rounded", colors.bgTertiary, colors.textPrimary)}>
          &ldquo;{title}&rdquo;
        </p>
        <p className={cn("text-xs mb-6", colors.textMuted)}>
          This action cannot be undone. All linked initiatives will also be deleted.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Delete Goal
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Initiative Modal
const AddInitiativeModal = ({ isOpen, onClose, onSave, goalId, productId, existingInitiativeIds = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [activeTab, setActiveTab] = useState('link');
  const [availableInitiatives, setAvailableInitiatives] = useState([]);
  const [selectedInitiativeId, setSelectedInitiativeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Create new initiative form
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  
  useEffect(() => {
    const loadInitiatives = async () => {
      if (!isOpen || !productId) return;
      
      setLoading(true);
      try {
        const res = await initiativesApi.listByProduct(productId);
        // Filter out initiatives already linked to a goal or already in this goal
        const unlinked = (res.data || []).filter(
          init => !existingInitiativeIds.includes(init.initiative_id) && !init.goal_id
        );
        setAvailableInitiatives(unlinked);
      } catch (err) {
        console.error('Failed to load initiatives:', err);
        setAvailableInitiatives([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitiatives();
  }, [isOpen, productId, existingInitiativeIds]);
  
  useEffect(() => {
    if (isOpen) {
      setSelectedInitiativeId('');
      setNewTitle('');
      setNewDescription('');
      setActiveTab('link');
    }
  }, [isOpen]);
  
  const handleLink = async () => {
    if (!selectedInitiativeId || !goalId) return;
    
    setSaving(true);
    try {
      await initiativesApi.update(selectedInitiativeId, { goal_id: goalId });
      toast.success('Initiative linked to goal');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to link initiative:', err);
      toast.error('Failed to link initiative');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCreate = async () => {
    if (!newTitle.trim() || !goalId) return;
    
    setSaving(true);
    try {
      await initiativesApi.create(goalId, {
        title: newTitle.trim(),
        description: newDescription.trim(),
        status: 'draft'
      });
      toast.success('Initiative created');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create initiative:', err);
      toast.error('Failed to create initiative');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Add Initiative</h3>
          <button onClick={onClose} className={colors.textMuted}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('link')}
            className={cn(
              "px-3 py-1.5 text-sm rounded-md transition-colors",
              activeTab === 'link' 
                ? cn("bg-blue-500 text-white") 
                : cn(colors.bgTertiary, colors.textSecondary)
            )}
          >
            Link Existing
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={cn(
              "px-3 py-1.5 text-sm rounded-md transition-colors",
              activeTab === 'create' 
                ? cn("bg-blue-500 text-white") 
                : cn(colors.bgTertiary, colors.textSecondary)
            )}
          >
            Create New
          </button>
        </div>
        
        {activeTab === 'link' ? (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
              </div>
            ) : availableInitiatives.length === 0 ? (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Rocket className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No unlinked initiatives available</p>
                <p className="text-xs mt-1">Create a new initiative instead</p>
              </div>
            ) : (
              <>
                <div>
                  <Label className={colors.textSecondary}>Select Initiative</Label>
                  <Select value={selectedInitiativeId} onValueChange={setSelectedInitiativeId}>
                    <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                      <SelectValue placeholder="Choose an initiative..." />
                    </SelectTrigger>
                    <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                      {availableInitiatives.map(init => (
                        <SelectItem key={init.initiative_id} value={init.initiative_id}>
                          {init.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                  <Button 
                    onClick={handleLink}
                    disabled={!selectedInitiativeId || saving}
                    className={cn(colors.accentGradient)}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Link Initiative
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className={colors.textSecondary}>Initiative Title *</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g., Q1 Churn Reduction Campaign"
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div>
              <Label className={colors.textSecondary}>Description</Label>
              <Textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Describe the initiative's purpose and scope..."
                rows={3}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button 
                onClick={handleCreate}
                disabled={!newTitle.trim() || saving}
                className={cn(colors.accentGradient)}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Initiative
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * GoalDetailModal
 * 
 * Displays and allows editing of goal details including KPIs.
 * 
 * STANDALONE MODE:
 * When `standalone={true}`, this modal operates independently without requiring
 * parent callbacks. Used by Executive AI to open goals in-place without
 * navigating away. In standalone mode:
 * - onSave/onDelete callbacks are ignored (API is called directly, toast shown)
 * - Modal closes automatically after save/delete
 * - No parent state refresh needed
 */
const GoalDetailModal = ({ 
  isOpen, 
  onClose, 
  goal, 
  productContext = {},
  onSave,
  onSaveAndClose,
  onDelete,
  onOpenInitiative,
  onInitiativeChange,  // Called when initiatives are linked/unlinked
  onKpiChange,  // Called when KPIs are added/deleted/modified (for cascading refresh)
  mode = 'edit', // 'edit' | 'create'
  standalone = false,  // When true, modal operates independently without parent callbacks
  zIndex = 50,  // Z-index for modal stacking
  suppressChildModals = false,  // When true, hides AI copilot and prevents entity edit modals from launching as children
  parentRefresh,  // Parent's refresh callbacks — when present, this modal is a child and calls these after save
}) => {
  const { getThemeColors } = useSafeMode();
  const { canCrudProducts, canUseAI } = usePermissions();
  const { user } = useAuth();
  const { formatDateTime } = useTimezone();
  const colors = getThemeColors();
  
  // Permission flags
  const canEdit = canCrudProducts;
  const canUseAICopilot = canUseAI;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddInitiativeModal, setShowAddInitiativeModal] = useState(false);
  const [showKpiSettingsModal, setShowKpiSettingsModal] = useState(false);
  const [showActivationValidation, setShowActivationValidation] = useState(false);
  const [activationErrors, setActivationErrors] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  
  // Stability warning modal state
  const [showStabilityWarning, setShowStabilityWarning] = useState(false);
  const [stabilityCheckData, setStabilityCheckData] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  
  // Track if KPIs were modified during this edit session (for cascading refresh)
  const [kpisModified, setKpisModified] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft',
    owner_id: '',
    kpis: [],  // Array of KPI objects
    target_date: ''  // Required for pace calculation
  });
  
  // Show AI Copilot only for existing goals in edit mode AND when NOT Active
  // When Active, no major editing is needed so hide the AI assistant
  const showAICopilot = mode !== 'create' && canUseAICopilot && !suppressChildModals && goal?.goal_id && formData.status !== 'active';
  
  // Check if fields should be disabled (Active goals lock editing on Overview)
  const isActiveGoal = formData.status === 'active';
  
  // Check if goal can be saved
  // Rules:
  // 1. Opens non-draft, moves TO draft → CAN SAVE (to allow "Move to Draft" action)
  // 2. Opens non-draft, moves to draft, moves BACK to non-draft → CANNOT SAVE
  // 3. Opens draft, moves to non-draft → CAN SAVE (normal activation flow)
  // 4. Opens draft, stays in draft → CAN SAVE (normal editing)
  // 5. Opens non-draft, stays non-draft → CANNOT SAVE (must move to draft first)
  const originalStatus = goal?.status || 'draft';
  const currentStatus = formData.status;
  const canSaveGoal = mode === 'create' || 
                      originalStatus === 'draft' ||  // Rules 3 & 4
                      currentStatus === 'draft';      // Rule 1
  
  // KPI editing state
  const [expandedKPIs, setExpandedKPIs] = useState({});  // Track which KPIs are expanded for editing
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Linked Initiatives state
  const [linkedInitiatives, setLinkedInitiatives] = useState([]);
  const [loadingInitiatives, setLoadingInitiatives] = useState(false);
  const [computedMetrics, setComputedMetrics] = useState(null);
  
  // Initiative search state (new inline search pattern)
  const [initiativeSearch, setInitiativeSearch] = useState('');
  const [showInitiativeDropdown, setShowInitiativeDropdown] = useState(false);
  const [allProductInitiatives, setAllProductInitiatives] = useState([]);
  
  // Inline create initiative state
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [newInitiativeTitle, setNewInitiativeTitle] = useState('');
  const [creatingInitiative, setCreatingInitiative] = useState(false);
  
  // Cascade unlink state
  const [cascadeModalOpen, setCascadeModalOpen] = useState(false);
  const [cascadeImpact, setCascadeImpact] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [pendingUnlinkInitiativeId, setPendingUnlinkInitiativeId] = useState(null);
  
  // KPI cascade delete state
  const [kpiCascadeModalOpen, setKpiCascadeModalOpen] = useState(false);
  const [kpiCascadeImpact, setKpiCascadeImpact] = useState(null);
  const [kpiCascadeLoading, setKpiCascadeLoading] = useState(false);
  const [pendingDeleteKpiId, setPendingDeleteKpiId] = useState(null);
  
  // Instability reason modal state
  const [instabilityReasonModal, setInstabilityReasonModal] = useState({
    open: false,
    reason: '',
    pendingAction: null  // 'save' or 'saveAndClose'
  });
  
  // Grace period expired flag - fetched from API when modal opens
  const [gracePeriodExpired, setGracePeriodExpired] = useState(false);
  
  // Ref for initiative dropdown click-outside handling
  const initiativeDropdownRef = useRef(null);
  
  // Pace data (At Risk status)
  const [paceData, setPaceData] = useState(null);
  
  // Click outside handler for initiative dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (initiativeDropdownRef.current && !initiativeDropdownRef.current.contains(event.target)) {
        setShowInitiativeDropdown(false);
        setShowInlineCreate(false);
        setNewInitiativeTitle('');
      }
    };
    
    if (showInitiativeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInitiativeDropdown]);
  
  // Handle form field changes
  // Validate goal can be activated
  const validateActivation = (data) => {
    const errors = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Must have an owner
    if (!data.owner_id) {
      errors.push({
        title: 'Owner Required',
        description: 'Please assign an owner to this goal before activating.'
      });
    }
    
    // Target date must be > today
    if (!data.target_date) {
      errors.push({
        title: 'Target Date Required',
        description: 'Please set a target date for this goal.'
      });
    } else {
      const targetDate = new Date(data.target_date);
      if (targetDate <= today) {
        errors.push({
          title: 'Target Date Must Be in the Future',
          description: 'The target date must be later than today to activate.'
        });
      }
    }
    
    // Must have at least one KPI
    if (!data.kpis || data.kpis.length === 0) {
      errors.push({
        title: 'At Least One KPI Required',
        description: 'Please add at least one Key Performance Indicator before activating.'
      });
    }
    
    return errors;
  };

  const handleChange = (field, value) => {
    // Intercept status change to "active" and validate
    if (field === 'status' && value === 'active' && formData.status !== 'active') {
      const errors = validateActivation(formData);
      if (errors.length > 0) {
        setActivationErrors(errors);
        setShowActivationValidation(true);
        return; // Don't change status
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  // Handle AI Co-Pilot apply updates
  const handleApplyUpdates = useCallback((updates) => {
    if (!updates) return;
    
    Object.entries(updates).forEach(([field, value]) => {
      // =====================================================================
      // SPECIAL OPERATIONS (prefixed with _) - from embedded entity actions
      // =====================================================================
      
      // Handle _updateKpis - update existing KPIs by item_id
      if (field === '_updateKpis' && Array.isArray(value)) {
        setFormData(prev => {
          const updatedKpis = (prev.kpis || []).map(kpi => {
            const update = value.find(u => u.item_id === kpi.kpi_id);
            if (update) {
              return {
                ...kpi,
                metric_name: update.metric_name !== undefined ? update.metric_name : kpi.metric_name,
                metric_format: update.metric_format !== undefined ? update.metric_format : kpi.metric_format,
                start_value: update.start_value !== undefined ? update.start_value : kpi.start_value,
                target_value: update.target_value !== undefined ? update.target_value : kpi.target_value,
                current_value: update.current_value !== undefined ? update.current_value : kpi.current_value,
                confidence: update.confidence !== undefined ? update.confidence : kpi.confidence
              };
            }
            return kpi;
          });
          return { ...prev, kpis: updatedKpis };
        });
        return;
      }
      
      // Handle _deleteKpis - remove KPIs by item_id
      if (field === '_deleteKpis' && Array.isArray(value)) {
        setFormData(prev => {
          const filteredKpis = (prev.kpis || []).filter(kpi => !value.includes(kpi.kpi_id));
          return { ...prev, kpis: filteredKpis };
        });
        setKpisModified(true);  // Track KPI change for cascade
        return;
      }
      
      // =====================================================================
      // STANDARD FIELD UPDATES
      // =====================================================================
      
      // Handle status - normalize to valid values and check for stability impact
      if (field === 'status' && value) {
        const validStatuses = ['draft', 'active', 'achieved', 'abandoned'];
        const normalizedStatus = value.toLowerCase().replace(/ /g, '_');
        if (validStatuses.includes(normalizedStatus)) {
          const currentStatus = formData.status;
          
          // Check if moving TO draft from active/achieved
          if (normalizedStatus === 'draft' && (currentStatus === 'active' || currentStatus === 'achieved')) {
            // Check stability impact asynchronously
            checkStabilityAndUpdateStatus(normalizedStatus);
            return; // Let the async function handle the update
          }
          
          setFormData(prev => ({ ...prev, status: normalizedStatus }));
        }
      }
      // Handle KPIs array
      else if (field === 'kpis' && Array.isArray(value)) {
        setFormData(prev => {
          const newKPIs = value.map(kpi => ({
            kpi_id: kpi.kpi_id || generateKPIId(),
            metric_name: kpi.metric_name || '',
            metric_format: kpi.metric_format || 'number',
            start_value: kpi.start_value || 0,
            target_value: kpi.target_value || 0,
            current_value: kpi.current_value || 0,
            confidence: kpi.confidence || 'medium'
          }));
          return { ...prev, kpis: [...prev.kpis, ...newKPIs] };
        });
        setKpisModified(true);  // Track KPI change for cascade
        // Auto-expand newly added KPIs
        value.forEach(kpi => {
          const kpiId = kpi.kpi_id || generateKPIId();
          setExpandedKPIs(prev => ({ ...prev, [kpiId]: true }));
        });
      }
      // Handle owner_id
      else if (field === 'owner_id') {
        setFormData(prev => ({ ...prev, owner_id: value === 'none' ? '' : value || '' }));
      }
      // Regular field update (skip special operation fields)
      else if (!field.startsWith('_')) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    });
    
    setHasChanges(true);
  }, []);
  
  // Async function to check stability impact before changing status to draft
  const checkStabilityAndUpdateStatus = useCallback(async (newStatus) => {
    if (!goal?.goal_id) {
      setFormData(prev => ({ ...prev, status: newStatus }));
      return;
    }
    
    try {
      const response = await goalsApi.checkStabilityImpact(goal.goal_id);
      const data = response.data;
      
      if (data.will_trigger_instability) {
        // Show warning modal instead of immediately changing
        setStabilityCheckData(data);
        setPendingStatusChange(newStatus);
        setShowStabilityWarning(true);
        return; // Don't update form data yet
      }
    } catch (err) {
      console.error('Failed to check stability impact:', err);
      // Continue with the change if check fails
    }
    
    // No instability warning needed, proceed with change
    setFormData(prev => ({ ...prev, status: newStatus }));
    setHasChanges(true);
  }, [goal?.goal_id]);
  
  // Refresh ONLY computed metrics and pace data (preserves unsaved form edits)
  // Use this after operations that shouldn't wipe form data
  const refreshComputedData = useCallback(async () => {
    if (!goal?.goal_id) return;
    try {
      const res = await goalsApi.get(goal.goal_id);
      if (res.data) {
        // Only update computed metrics and pace data - NOT formData
        setComputedMetrics(res.data.computed_metrics || null);
        setPaceData(res.data.pace || null);
        
        // Notify parent of data change so GoalCard refreshes
        if (!standalone && onSave) {
          onSave(res.data, { kpiDataLogged: true });
        }
      }
    } catch (err) {
      console.error('Failed to refresh computed data:', err);
    }
  }, [goal?.goal_id, standalone, onSave]);
  
  // Refresh ALL goal data including form (use for initial load or explicit reset)
  const refreshGoalData = useCallback(async () => {
    if (!goal?.goal_id) return;
    try {
      const res = await goalsApi.get(goal.goal_id);
      if (res.data) {
        // Migrate legacy kpi_config to kpis array if needed
        let kpis = res.data.kpis || [];
        if (!kpis.length && res.data.kpi_config && res.data.kpi_config.metric_name) {
          kpis = [{
            ...res.data.kpi_config,
            kpi_id: res.data.kpi_config.kpi_id || generateKPIId()
          }];
        }
        
        setFormData({
          title: res.data.title || '',
          description: res.data.description || '',
          status: res.data.status || 'draft',
          owner_id: res.data.owner_id || '',
          kpis: kpis,
          target_date: res.data.target_date 
            ? new Date(res.data.target_date).toISOString().split('T')[0] 
            : ''
        });
        setComputedMetrics(res.data.computed_metrics || null);
        setPaceData(res.data.pace || null);
      }
    } catch (err) {
      console.error('Failed to refresh goal:', err);
    }
  }, [goal?.goal_id]);
  
  // Handle KPI changes (for multiple KPIs)
  const handleKPIChange = (kpiId, field, value) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.map(kpi => 
        kpi.kpi_id === kpiId ? { ...kpi, [field]: value } : kpi
      )
    }));
    setHasChanges(true);
    setKpisModified(true);  // Track KPI change for cascade
  };
  
  // Add new KPI
  const handleAddKPI = () => {
    const newKPI = {
      kpi_id: generateKPIId(),
      metric_name: '',
      description: '',  // Optional description for the KPI
      metric_format: 'number',
      direction: 'higher_is_better',
      measurement_interval: 'monthly',
      start_value: 0,
      target_value: 0,
      current_value: 0,
      confidence: 'medium',
      data_points: []
    };
    setFormData(prev => ({
      ...prev,
      kpis: [...prev.kpis, newKPI]
    }));
    setExpandedKPIs(prev => ({ ...prev, [newKPI.kpi_id]: true }));  // Auto-expand new KPI
    setHasChanges(true);
  };
  
  // Check KPI cascade impact before deleting (for existing goals)
  const handleRemoveKPIClick = async (kpiId) => {
    // For new goals (create mode), just remove locally
    if (mode === 'create') {
      removeKPILocally(kpiId);
      return;
    }
    
    // For existing goals, check cascade impact
    setKpiCascadeLoading(true);
    setPendingDeleteKpiId(kpiId);
    
    try {
      const response = await goalsApi.getKpiCascadeImpact(goal.goal_id, kpiId);
      setKpiCascadeImpact(response.data);
      setKpiCascadeModalOpen(true);
    } catch (err) {
      console.error('Failed to get KPI cascade impact:', err);
      // If we can't get impact, show basic confirmation
      const kpi = formData.kpis.find(k => k.kpi_id === kpiId);
      setKpiCascadeImpact({
        kpi_name: kpi?.metric_name || 'KPI',
        goal_title: formData.title,
        has_impact: false,
        impact: { initiatives: { count: 0 }, epics: { count: 0 }, features: { count: 0 } }
      });
      setKpiCascadeModalOpen(true);
    } finally {
      setKpiCascadeLoading(false);
    }
  };
  
  // Remove KPI locally (for create mode or after cascade delete)
  const removeKPILocally = (kpiId) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter(kpi => kpi.kpi_id !== kpiId)
    }));
    setExpandedKPIs(prev => {
      const updated = { ...prev };
      delete updated[kpiId];
      return updated;
    });
    setHasChanges(true);
  };
  
  // Execute KPI cascade delete - now uses optimistic UI pattern
  // Actual cascade happens on Save via the PATCH endpoint's KPI change detection
  const executeKpiCascadeDelete = async () => {
    if (!pendingDeleteKpiId) return;
    
    // For new goals (create mode), just remove locally
    if (mode === 'create' || !goal?.goal_id) {
      removeKPILocally(pendingDeleteKpiId);
      setKpiCascadeModalOpen(false);
      setPendingDeleteKpiId(null);
      setKpiCascadeImpact(null);
      toast.success('KPI removed');
      return;
    }
    
    // For existing goals in draft mode:
    // Remove locally (optimistic UI) - cascade will happen on Save
    removeKPILocally(pendingDeleteKpiId);
    
    // Close the modal
    setKpiCascadeModalOpen(false);
    setPendingDeleteKpiId(null);
    setKpiCascadeImpact(null);
    
    toast.success('KPI removed - save to apply changes');
  };
  
  // Cancel KPI cascade delete
  const cancelKpiCascadeDelete = () => {
    setKpiCascadeModalOpen(false);
    setPendingDeleteKpiId(null);
    setKpiCascadeImpact(null);
  };
  
  // Toggle KPI expansion
  const toggleKPIExpanded = (kpiId) => {
    setExpandedKPIs(prev => ({
      ...prev,
      [kpiId]: !prev[kpiId]
    }));
  };
  
  // Load team members
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const res = await teamApi.list();
        const activeMembers = (res.data?.members || []).filter(m => m.status === 'active');
        setTeamMembers(activeMembers);
      } catch (err) {
        console.error('Failed to load team:', err);
      } finally {
        setLoadingTeam(false);
      }
    };
    
    if (isOpen) {
      loadTeam();
    }
  }, [isOpen]);
  
  // Load goal data when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    if (mode === 'create') {
      // New goal - set defaults
      setFormData({
        title: '',
        description: '',
        status: 'draft',
        owner_id: user?.user_id || '',
        kpis: [],  // Start with no KPIs
        target_date: ''  // Required - user must set
      });
      setExpandedKPIs({});
      setLinkedInitiatives([]);
      setComputedMetrics(null);
      setPaceData(null);
      setGracePeriodExpired(false);  // Not applicable for create mode
      setHasChanges(false);
      setKpisModified(false);  // Reset KPI modification tracking
      setActiveTab('overview');
    } else if (goal?.goal_id) {
      // Existing goal - ALWAYS fetch fresh data from API to avoid stale prop issues
      const loadFreshGoalData = async () => {
        try {
          const res = await goalsApi.get(goal.goal_id);
          const freshGoal = res.data;
          
          // Migrate legacy kpi_config to kpis array if needed
          let kpis = freshGoal.kpis || [];
          if (!kpis.length && freshGoal.kpi_config && freshGoal.kpi_config.metric_name) {
            kpis = [{
              ...freshGoal.kpi_config,
              kpi_id: freshGoal.kpi_config.kpi_id || generateKPIId()
            }];
          }
          
          setFormData({
            title: freshGoal.title || '',
            description: freshGoal.description || '',
            status: freshGoal.status || 'draft',
            owner_id: freshGoal.owner_id || '',
            kpis: kpis,
            target_date: freshGoal.target_date 
              ? new Date(freshGoal.target_date).toISOString().split('T')[0] 
              : ''
          });
          setExpandedKPIs({});  // Start with all collapsed
          setComputedMetrics(freshGoal.computed_metrics || null);
          setPaceData(freshGoal.pace || null);
          setGracePeriodExpired(freshGoal.grace_period_expired === true);  // Store grace period flag from API
          setHasChanges(false);
          setKpisModified(false);  // Reset KPI modification tracking
          setActiveTab('overview');
          
          // Load initiatives
          loadLinkedInitiatives(freshGoal.goal_id);
        } catch (err) {
          console.error('Failed to load fresh goal data:', err);
          // Fallback to prop data if API fails
          let kpis = goal.kpis || [];
          if (!kpis.length && goal.kpi_config && goal.kpi_config.metric_name) {
            kpis = [{
              ...goal.kpi_config,
              kpi_id: goal.kpi_config.kpi_id || generateKPIId()
            }];
          }
          
          setFormData({
            title: goal.title || '',
            description: goal.description || '',
            status: goal.status || 'draft',
            owner_id: goal.owner_id || '',
            kpis: kpis,
            target_date: goal.target_date 
              ? new Date(goal.target_date).toISOString().split('T')[0] 
              : ''
          });
          setExpandedKPIs({});
          setComputedMetrics(goal.computed_metrics || null);
          setPaceData(goal.pace || null);
          setGracePeriodExpired(goal.grace_period_expired === true);  // Fallback: use prop if available
          setHasChanges(false);
          setKpisModified(false);  // Reset KPI modification tracking
          setActiveTab('overview');
          
          loadLinkedInitiatives(goal.goal_id);
        }
      };
      
      loadFreshGoalData();
      
      // Load all product initiatives for search dropdown
      if (productContext?.product_id) {
        initiativesApi.listByProduct(productContext.product_id)
          .then(res => setAllProductInitiatives(res.data || []))
          .catch(err => {
            console.error('Failed to load product initiatives:', err);
            setAllProductInitiatives([]);
          });
      }
    }
  }, [isOpen, goal?.goal_id, mode, user?.user_id, productContext?.product_id]);
  
  // Reset state when modal closes to prevent stale data flash on reopen
  useEffect(() => {
    if (!isOpen) {
      // Clear data immediately when modal closes
      setFormData({
        title: '',
        description: '',
        status: 'draft',
        owner_id: '',
        kpis: [],
        target_date: ''
      });
      setPaceData(null);
      setComputedMetrics(null);
      setLinkedInitiatives([]);
      setExpandedKPIs({});
      setActiveTab('overview');
      setHasChanges(false);
      setGracePeriodExpired(false);  // Reset grace period flag
    }
  }, [isOpen]);
  
  const loadLinkedInitiatives = async (goalId) => {
    setLoadingInitiatives(true);
    try {
      const res = await initiativesApi.list(goalId);
      setLinkedInitiatives(res.data || []);
    } catch (err) {
      console.error('Failed to load initiatives:', err);
      setLinkedInitiatives([]);
    } finally {
      setLoadingInitiatives(false);
    }
  };
  
  // Load all product initiatives for the search dropdown
  const loadAllProductInitiatives = async () => {
    if (!productContext?.product_id) return;
    try {
      const res = await initiativesApi.listByProduct(productContext.product_id);
      setAllProductInitiatives(res.data || []);
    } catch (err) {
      console.error('Failed to load product initiatives:', err);
      setAllProductInitiatives([]);
    }
  };
  
  // Link an existing initiative to this goal
  const handleLinkInitiative = async (initiative) => {
    if (!goal?.goal_id) return;
    try {
      // Get the initiative's current goal_ids and add this goal
      const currentGoalIds = initiative.goal_ids || (initiative.goal_id ? [initiative.goal_id] : []);
      const newGoalIds = [...new Set([...currentGoalIds, goal.goal_id])];
      
      await initiativesApi.update(initiative.initiative_id, { goal_ids: newGoalIds });
      toast.success('Initiative linked');
      
      // Refresh linked initiatives
      loadLinkedInitiatives(goal.goal_id);
      setInitiativeSearch('');
      setShowInitiativeDropdown(false);
      
      // Notify parent to refresh initiatives collection (for count update on goal card)
      if (!standalone && onInitiativeChange) {
        onInitiativeChange();
      } else if (!onInitiativeChange) {
        // No callback - might be standalone mode or missing prop
      }
    } catch (err) {
      console.error('Failed to link initiative:', err);
      toast.error('Failed to link initiative');
    }
  };
  
  // Create new initiative inline and link to this goal
  const handleInlineCreateInitiative = async () => {
    if (!newInitiativeTitle.trim() || !goal?.goal_id) return;
    
    setCreatingInitiative(true);
    try {
      // Create new initiative linked to this goal
      await initiativesApi.create(goal.goal_id, {
        title: newInitiativeTitle.trim(),
        description: '',
        status: 'draft'
      });
      toast.success('Initiative created and linked');
      
      // Reset and refresh
      setNewInitiativeTitle('');
      setShowInlineCreate(false);
      setShowInitiativeDropdown(false);
      setInitiativeSearch('');
      loadLinkedInitiatives(goal.goal_id);
      loadAllProductInitiatives();
      
      // Notify parent to refresh initiatives collection (for count update on goal card)
      if (!standalone && onInitiativeChange) {
        onInitiativeChange();
      }
    } catch (err) {
      console.error('Failed to create initiative:', err);
      toast.error('Failed to create initiative');
    } finally {
      setCreatingInitiative(false);
    }
  };
  
  // Save goal - returns true if successful, false otherwise
  // Now also notifies parent for entity-level refresh (parent UI updates immediately)
  // Check if moving to draft will trigger instability
  const willTriggerInstability = () => {
    // Only for existing goals (not create mode)
    if (mode === 'create') return false;
    
    // Check if status is changing TO draft
    const originalStatus = goal?.status;
    const newStatus = formData.status;
    if (newStatus !== 'draft' || originalStatus === 'draft') return false;
    
    // Check if grace period has expired (using state fetched from API on modal open)
    return gracePeriodExpired === true;
  };
  
  // Internal save function that performs the actual API call
  const performSave = async (instabilityReason = null) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return { success: false };
    }
    
    setSaving(true);
    try {
      // Prepare payload with target_date as ISO string (can be null)
      const payload = {
        ...formData,
        target_date: formData.target_date 
          ? new Date(formData.target_date).toISOString() 
          : null
      };
      
      // Add instability reason if provided
      if (instabilityReason) {
        payload.instability_reason = instabilityReason;
      }
      
      if (mode === 'create') {
        const res = await goalsApi.create(productContext.product_id, payload);
        setHasChanges(false);
        toast.success('Goal created');
        
        // Notify parent to refresh collection (new entity needs to be added to list)
        if (!standalone) {
          if (parentRefresh?.goal) {
            parentRefresh.goal();
            parentRefresh.markDirty?.('goals');
          } else if (onSave) {
            await onSave(res.data);
          }
        }
        
        return { success: true, data: res.data, isCreate: true };
      } else {
        const updateResponse = await goalsApi.update(goal.goal_id, payload);
        setHasChanges(false);
        
        // Refresh local state with the updated data from the response
        if (updateResponse?.data) {
          const updatedGoal = updateResponse.data;
          // Update form data with saved values
          setFormData({
            title: updatedGoal.title || '',
            description: updatedGoal.description || '',
            status: updatedGoal.status || 'draft',
            owner_id: updatedGoal.owner_id || '',
            kpis: updatedGoal.kpis || [],
            target_date: updatedGoal.target_date 
              ? new Date(updatedGoal.target_date).toISOString().split('T')[0] 
              : ''
          });
          setComputedMetrics(updatedGoal.computed_metrics || null);
          setPaceData(updatedGoal.pace || null);
          
          // Notify parent for entity-level refresh (parent UI updates immediately)
          if (!standalone) {
            if (parentRefresh?.goal) {
              parentRefresh.goal();
              parentRefresh.markDirty?.('goals');
              if (kpisModified) parentRefresh.markDirty?.('kpis');
            } else if (onSave) {
              await onSave(updatedGoal, { kpisChanged: kpisModified });
            }
          }
          // Trigger cascade refresh if KPIs were modified
          if (kpisModified && onKpiChange) {
            onKpiChange();
          }
        } else {
          // Fallback to refresh if no response data
          await refreshGoalData();
          // Notify parent to refresh collection as fallback
          if (!standalone) {
            if (parentRefresh?.goal) {
              parentRefresh.goal();
              parentRefresh.markDirty?.('goals');
              if (kpisModified) parentRefresh.markDirty?.('kpis');
            } else if (onSave) {
              await onSave(null, { kpisChanged: kpisModified });
            }
          }
          // Trigger cascade refresh if KPIs were modified
          if (kpisModified && onKpiChange) {
            onKpiChange();
          }
        }
        
        // Reset kpisModified after successful save
        setKpisModified(false);
        
        toast.success('Goal saved');
        return { success: true, data: updateResponse?.data, isCreate: false, kpisChanged: kpisModified };
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save goal');
      return { success: false };
    } finally {
      setSaving(false);
    }
  };
  
  // Main save handler - checks for instability trigger first
  const handleSave = async () => {
    // Check if this will trigger instability and needs a reason
    if (willTriggerInstability()) {
      setInstabilityReasonModal({
        open: true,
        reason: '',
        pendingAction: 'save'
      });
      return { success: false, pending: true };
    }
    
    return performSave();
  };
  
  // Save and close - only closes if save was successful
  const handleSaveAndClose = async () => {
    // Check if this will trigger instability and needs a reason
    if (willTriggerInstability()) {
      setInstabilityReasonModal({
        open: true,
        reason: '',
        pendingAction: 'saveAndClose'
      });
      return;
    }
    
    const result = await performSave();
    if (result?.success) {
      setShowUnsavedWarning(false);
      
      // Call parent callback for close (onSave already called in handleSave)
      // Use onSaveAndClose if provided for any close-specific logic
      if (!standalone && onSaveAndClose) {
        await onSaveAndClose(result.data, { kpisChanged: result.kpisChanged });
      }
      
      performClose();
    }
  };
  
  // Handle instability reason modal confirm
  const handleInstabilityReasonConfirm = async () => {
    const reason = instabilityReasonModal.reason.trim();
    const pendingAction = instabilityReasonModal.pendingAction;
    
    // Close the modal
    setInstabilityReasonModal({ open: false, reason: '', pendingAction: null });
    
    // Perform the save with the reason
    const result = await performSave(reason);
    
    // If saveAndClose was the pending action, close on success
    if (result?.success && pendingAction === 'saveAndClose') {
      setShowUnsavedWarning(false);
      if (!standalone && onSaveAndClose) {
        await onSaveAndClose(result.data, { kpisChanged: result.kpisChanged });
      }
      performClose();
    }
  };
  
  // Delete goal
  const handleDelete = async () => {
    if (!goal?.goal_id) return;
    
    setDeleting(true);
    try {
      await goalsApi.delete(goal.goal_id);
      toast.success('Goal deleted');
      setShowDeleteConfirm(false);
      
      // STANDALONE MODE: Skip parent callback
      if (!standalone) {
        onDelete?.();
      }
      onClose();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete goal');
    } finally {
      setDeleting(false);
    }
  };
  
  // Check cascade impact before unlinking
  const handleUnlinkInitiativeClick = async (initiativeId) => {
    setCascadeLoading(true);
    setPendingUnlinkInitiativeId(initiativeId);
    
    try {
      const response = await goalsApi.getCascadeImpact(goal.goal_id, initiativeId);
      setCascadeImpact(response.data);
      setCascadeModalOpen(true);
    } catch (err) {
      console.error('Failed to get cascade impact:', err);
      // If we can't get impact, still show confirmation but without details
      setCascadeImpact({
        goal_title: goal.title,
        initiative_title: linkedInitiatives.find(i => i.initiative_id === initiativeId)?.title || 'Initiative',
        has_impact: false,
        impact: { subscribed_kpis: { count: 0 }, epics: { count: 0 }, features: { count: 0 } }
      });
      setCascadeModalOpen(true);
    } finally {
      setCascadeLoading(false);
    }
  };
  
  // Execute cascade unlink after user confirmation
  const executeUnlinkInitiative = async () => {
    if (!pendingUnlinkInitiativeId) return;
    
    setCascadeLoading(true);
    try {
      await goalsApi.executeCascadeUnlink(goal.goal_id, pendingUnlinkInitiativeId);
      toast.success('Initiative unlinked and relationships cleaned up');
      loadLinkedInitiatives(goal.goal_id);
      // Refresh computed metrics
      const goalResponse = await goalsApi.get(goal.goal_id);
      if (goalResponse.data) {
        setComputedMetrics(goalResponse.data.computed_metrics || null);
        // Notify parent to update the goal card
        if (!standalone && onSave) {
          await onSave(goalResponse.data);
        }
      }
      // Notify parent to refresh initiatives collection (for count update on goal card)
      if (!standalone && onInitiativeChange) {
        onInitiativeChange();
      }
    } catch (err) {
      console.error('Failed to unlink initiative:', err);
      toast.error('Failed to unlink initiative');
    } finally {
      setCascadeLoading(false);
      setCascadeModalOpen(false);
      setPendingUnlinkInitiativeId(null);
      setCascadeImpact(null);
    }
  };
  
  // Cancel cascade unlink
  const cancelCascadeUnlink = () => {
    setCascadeModalOpen(false);
    setPendingUnlinkInitiativeId(null);
    setCascadeImpact(null);
  };
  
  // Close modal - check for unsaved changes first
  const handleClose = () => {
    // First check for unsaved form changes
    if (hasChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    // No longer need close guard - pending actions persist in chat log
    performClose();
  };
  
  // Actually perform the close action
  const performClose = () => {
    setShowUnsavedWarning(false);
    onClose();
  };
  
  // Get status config
  const getStatusConfig = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };
  
  // Get investment badge display
  const getInvestmentBadge = () => {
    const badge = computedMetrics?.investment_badge || 'low';
    const pct = computedMetrics?.investment_pct || 0;
    
    const badges = {
      high: { icon: '🔥', label: 'High Bet', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      medium: { icon: '💡', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      low: { icon: '🌱', label: 'Low', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
    };
    
    const config = badges[badge] || badges.low;
    return (
      <span className={cn("px-2 py-1 rounded-full text-xs font-medium", config.color)}>
        {config.icon} {config.label} ({pct}%)
      </span>
    );
  };
  
  // Get KPI status display
  const getKPIStatus = () => {
    const status = computedMetrics?.kpi_status || 'not_set';
    const kpi = formData.kpi_config;
    
    if (status === 'not_set' || !kpi?.target_value) {
      return (
        <span className="text-gray-400 text-sm flex items-center gap-1">
          <Minus className="w-4 h-4" />
          KPI not configured
        </span>
      );
    }
    
    const isOnTrack = status === 'on_track';
    
    return (
      <div className="flex items-center gap-2">
        <span className={cn("text-sm font-medium", isOnTrack ? "text-green-600" : "text-red-600")}>
          Target: {kpi.target_value} | Current: {kpi.current_value}
        </span>
        {isOnTrack ? (
          <TrendingUp className="w-4 h-4 text-green-500" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-500" />
        )}
        <span className={cn("text-xs px-2 py-0.5 rounded", isOnTrack ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>
          {isOnTrack ? 'On Track' : 'Off Track'}
        </span>
      </div>
    );
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalZIndexProvider zIndex={zIndex}>
    <div className="fixed inset-0" style={{ zIndex }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Modal - ~80% screen size like Feature modal */}
      <div className={cn("absolute inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden", colors.bgCard)}>
        {/* Header */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
              <Target className={cn("w-5 h-5", colors.textAccent)} />
            </div>
            <div className="flex items-center gap-3">
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                  {mode === 'create' ? 'Create Goal' : 'Edit Goal'}
                </h2>
                {mode !== 'create' && formData.title && (
                  <p className={cn("text-sm truncate max-w-[400px]", colors.textMuted)}>{formData.title}</p>
                )}
              </div>
              {goal?.is_shifted && (
                <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium flex items-center gap-1">
                  ⚠️ Shifted
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'edit' && canEdit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                title="Delete goal"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {/* Save buttons - only enabled in draft mode */}
            {canSaveGoal ? (
              <>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  variant={hasChanges ? "default" : "outline"}
                  className={cn(hasChanges ? colors.accentGradient : "opacity-50")}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span className="ml-1">Save</span>
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAndClose}
                  disabled={saving || !hasChanges}
                  variant={hasChanges ? "default" : "outline"}
                  className={cn(hasChanges ? colors.accentGradient : "opacity-50")}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span className="ml-1">Save & Close</span>
                </Button>
              </>
            ) : (
              <Button 
                size="sm"
                variant="outline"
                onClick={() => {
                  setFormData(prev => ({ ...prev, status: 'draft' }));
                  setHasChanges(true);
                }}
                className={cn("flex items-center gap-2", colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
                title="Move goal to Draft status to make changes"
              >
                <Lock className="w-4 h-4" />
                <span>Move to Draft to edit</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClose}
              className={cn(colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
            >
              Close
            </Button>
          </div>
        </div>
        
        {/* Content - 60/40 Split */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Form Fields (60%) */}
          <div className={cn(
            "overflow-y-auto flex flex-col",
            showAICopilot ? "w-[60%] border-r" : "w-full",
            colors.borderPrimary
          )}>
            {/* Tab Navigation - Pill Style at TOP */}
            <div className="px-6 pt-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className={cn("mb-4 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                  <TabsTrigger 
                    value="overview" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger 
                    value="kpi" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    KPIs ({formData.kpis.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="initiatives" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    Initiatives ({linkedInitiatives.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="attachments" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    <Paperclip className="w-3.5 h-3.5 mr-1" />
                    Attachments
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notes" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1" />
                    Notes
                  </TabsTrigger>
                  <TabsTrigger 
                    value="stability" 
                    className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                  >
                    Stability Log
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* KPI Status Summary Banner - Only show when Active with KPIs */}
                  {formData.status === 'active' && paceData && formData.kpis.length > 0 && (
                    <div className={cn(
                      "p-4 rounded-lg border",
                      paceData.pace_status === 'off_track' 
                        ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700"
                        : paceData.pace_status === 'insufficient_data' || paceData.pace_status === 'no_data'
                          ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                          : "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                    )}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            paceData.pace_status === 'off_track'
                              ? "bg-red-100 dark:bg-red-900/40"
                              : paceData.pace_status === 'insufficient_data' || paceData.pace_status === 'no_data'
                                ? "bg-amber-100 dark:bg-amber-900/40"
                                : "bg-green-100 dark:bg-green-900/40"
                          )}>
                            {paceData.pace_status === 'on_track' ? (
                              <TrendingUp className="w-5 h-5 text-green-600" />
                            ) : paceData.pace_status === 'off_track' ? (
                              <TrendingDown className="w-5 h-5 text-red-600" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className={cn(
                              "font-semibold",
                              paceData.pace_status === 'off_track' 
                                ? "text-red-800 dark:text-red-200" 
                                : paceData.pace_status === 'on_track'
                                  ? "text-green-800 dark:text-green-200"
                                  : "text-amber-800 dark:text-amber-200"
                            )}>
                              {/* Show method prefix: "Majority: On Track" */}
                              {paceData.aggregate?.method_label}: {paceData.pace_status_label}
                              {paceData.days_remaining !== null && (
                                <span className="font-normal ml-2">• {paceData.days_remaining} days remaining</span>
                              )}
                            </p>
                            <p className={cn(
                              "text-sm mt-0.5",
                              paceData.pace_status === 'off_track'
                                ? "text-red-700 dark:text-red-300"
                                : paceData.pace_status === 'on_track'
                                  ? "text-green-700 dark:text-green-300"
                                  : "text-amber-700 dark:text-amber-300"
                            )}>
                              {paceData.aggregate?.summary || 'No KPI status data'}
                            </p>
                          </div>
                        </div>
                        {/* KPI Breakdown Pills */}
                        {paceData.aggregate?.breakdown && (
                          <div className="flex items-center gap-2">
                            {paceData.aggregate.breakdown.on_track > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400">
                                {paceData.aggregate.breakdown.on_track} On Track
                              </span>
                            )}
                            {paceData.aggregate.breakdown.off_track > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400">
                                {paceData.aggregate.breakdown.off_track} Off Track
                              </span>
                            )}
                            {paceData.aggregate.breakdown.insufficient_data > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                                {paceData.aggregate.breakdown.insufficient_data} Insufficient
                              </span>
                            )}
                            {paceData.aggregate.breakdown.no_data > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                {paceData.aggregate.breakdown.no_data} No Data
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Key Attributes Section */}
                  <div className="space-y-4">
                    {/* Title Row */}
                    <div>
                      <Label className={colors.textSecondary}>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="e.g., Crush Churn in Q1"
                        disabled={isActiveGoal}
                        className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      />
                    </div>
                    
                    {/* Status & Owner & Target Date Row - side by side */}
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div className="flex flex-col">
                        <Label className={colors.textSecondary}>Status</Label>
                        <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            {STATUS_OPTIONS.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                <div className="flex items-center gap-2">
                                  <span className={cn("w-2 h-2 rounded-full", s.color)} />
                                  {s.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                          <User className="w-3 h-3" /> Owner
                        </Label>
                        <Select 
                          value={formData.owner_id || 'none'} 
                          onValueChange={(v) => handleChange('owner_id', v === 'none' ? '' : v)}
                          disabled={isActiveGoal}
                        >
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select owner..." />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {teamMembers.map(m => (
                              <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                          <Calendar className="w-3 h-3" /> Target Date
                        </Label>
                        <div className="mt-1">
                          <DatePickerWithMarkers
                            value={formData.target_date}
                            onChange={(date) => handleChange('target_date', date)}
                            parentDeadlines={[]}
                            placeholder="Select target date"
                            disabled={isActiveGoal}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Investment Badge - Read only */}
                    {mode === 'edit' && computedMetrics && (
                      <div className="flex items-center gap-4">
                        <span className={cn("text-sm", colors.textSecondary)}>Investment:</span>
                        {getInvestmentBadge()}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <Label className={colors.textSecondary}>Description</Label>
                    {isActiveGoal ? (
                      <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
                        {formData.description ? (
                          <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                            <ReactMarkdown>{formData.description}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className={cn("text-sm", colors.textMuted)}>No description provided</p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1">
                        <MarkdownEditor
                          content={formData.description || ''}
                          onChange={(value) => handleChange('description', value)}
                          placeholder="Describe the strategic objective..."
                          minHeight={250}
                          disabled={isActiveGoal}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* KPI Tab */}
              {activeTab === 'kpi' && (
                <div className="space-y-4">
                  {/* Header with title and action buttons */}
                  <div className="flex items-center justify-between">
                    <h3 className={cn("text-sm font-semibold", colors.textPrimary)}>
                      Key Performance Indicators ({formData.kpis.length})
                    </h3>
                    <div className="flex items-center gap-2">
                      {/* Settings Button - Only show in active mode */}
                      {formData.status === 'active' && formData.kpis.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowKpiSettingsModal(true)}
                          className={cn("gap-1", colors.borderPrimary)}
                          title="KPI Status Settings"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </Button>
                      )}
                      {/* Add KPI Button - Only show in draft mode */}
                      {formData.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleAddKPI}
                          className={cn("gap-1", colors.borderPrimary)}
                        >
                          <Plus className="w-4 h-4" />
                          Add KPI
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* KPIs List */}
                  {formData.kpis.length === 0 ? (
                    <div className={cn("text-center py-8 rounded-lg border border-dashed", colors.borderPrimary)}>
                      <Target className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                      <p className={cn("text-sm", colors.textMuted)}>No KPIs defined yet</p>
                      {formData.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleAddKPI}
                          className="mt-3"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Your First KPI
                        </Button>
                      )}
                    </div>
                  ) : formData.status === 'active' ? (
                    /* ============================================
                       ACTIVE MODE: Chart-first scoreboard view
                       ============================================ */
                    <div className="space-y-4">
                      {formData.kpis.map((kpi) => {
                        const computed = computedMetrics?.kpis_computed?.find(k => k.kpi_id === kpi.kpi_id);
                        const paceDetail = paceData?.kpi_pace_details?.find(p => p.kpi_id === kpi.kpi_id);
                        return (
                          <ActiveKPICard
                            key={kpi.kpi_id}
                            goalId={goal?.goal_id}
                            kpi={kpi}
                            computed={computed}
                            paceDetail={paceDetail}
                            colors={colors}
                            goalStartDate={goal?.first_activated_at}
                            targetDate={formData.target_date}
                            defaultExpanded={false}
                            aggregateMethodLabel={paceData?.aggregate?.method_label}
                            isLoading={!paceData}
                            onRefresh={async () => {
                              try {
                                const response = await goalsApi.getDetails(goal.goal_id);
                                // Backend returns goal data at root level, not nested under 'goal' key
                                if (response.data?.kpis) {
                                  setFormData(prev => ({
                                    ...prev,
                                    kpis: response.data.kpis
                                  }));
                                  setComputedMetrics(response.data.computed_metrics);
                                  setPaceData(response.data.pace);
                                  
                                  // Notify parent of data change so GoalCard refreshes
                                  if (!standalone && onSave) {
                                    onSave(response.data, { kpiDataLogged: true });
                                  }
                                }
                              } catch (err) {
                                console.error('Failed to refresh:', err);
                              }
                            }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.kpis.map((kpi, index) => {
                        const isExpanded = expandedKPIs[kpi.kpi_id];
                        const computed = computedMetrics?.kpis_computed?.find(k => k.kpi_id === kpi.kpi_id);
                        const FormatIcon = METRIC_FORMAT_OPTIONS.find(f => f.value === kpi.metric_format)?.icon || Hash;
                        const isLocked = formData.status !== 'draft';
                        
                        return (
                          <div 
                            key={kpi.kpi_id}
                            className={cn("rounded-lg border overflow-hidden", colors.borderPrimary, colors.bgTertiary)}
                          >
                            {/* Collapsed Header */}
                            <div 
                              className={cn(
                                "flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50",
                                isExpanded && "border-b " + colors.borderPrimary
                              )}
                              onClick={() => toggleKPIExpanded(kpi.kpi_id)}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                {isExpanded ? (
                                  <ChevronDown className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
                                ) : (
                                  <ChevronRight className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
                                )}
                                <FormatIcon className={cn("w-4 h-4 flex-shrink-0", colors.textAccent)} />
                                <div className="min-w-0 flex-1">
                                  <p className={cn("font-medium truncate", colors.textPrimary)}>
                                    {kpi.metric_name || `KPI ${index + 1}`}
                                  </p>
                                  {!isExpanded && (
                                    <div className="flex items-center gap-2">
                                      {kpi.description && (
                                        <p className={cn("text-xs truncate max-w-[200px]", colors.textMuted)} title={kpi.description}>
                                          {kpi.description.length > 40 ? `${kpi.description.substring(0, 40)}...` : kpi.description}
                                        </p>
                                      )}
                                      {kpi.target_value > 0 && (
                                        <p className={cn("text-xs whitespace-nowrap", colors.textMuted)}>
                                          {formatMetricValue(kpi.start_value || 0, kpi.metric_format)} → {formatMetricValue(kpi.target_value, kpi.metric_format)}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isLocked && (
                                  <span className="text-xs text-amber-600 dark:text-amber-400">🔒</span>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleKPIExpanded(kpi.kpi_id); }}
                                  className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (!isLocked) handleRemoveKPIClick(kpi.kpi_id); }}
                                  disabled={isLocked}
                                  className={cn(
                                    "p-1 rounded text-red-500",
                                    isLocked ? "opacity-30 cursor-not-allowed" : "hover:bg-red-100 dark:hover:bg-red-900/30"
                                  )}
                                  title={isLocked ? "Cannot delete KPI while goal is not in Draft status" : "Delete KPI"}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Expanded Edit Form */}
                            {isExpanded && (
                              <div className="p-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Metric Name {isLocked && "🔒"}
                                    </Label>
                                    <Input
                                      value={kpi.metric_name || ''}
                                      onChange={(e) => handleKPIChange(kpi.kpi_id, 'metric_name', e.target.value)}
                                      placeholder="e.g., Revenue Growth, Churn Rate"
                                      disabled={isLocked}
                                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                                      data-testid={`kpi-metric-name-${kpi.kpi_id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Format {isLocked && "🔒"}
                                    </Label>
                                    <Select 
                                      value={kpi.metric_format || 'number'} 
                                      onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'metric_format', v)}
                                      disabled={isLocked}
                                    >
                                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                                        {METRIC_FORMAT_OPTIONS.map(f => {
                                          const Icon = f.icon;
                                          return (
                                            <SelectItem key={f.value} value={f.value}>
                                              <div className="flex items-center gap-2">
                                                <Icon className="w-4 h-4" />
                                                <span>{f.label}</span>
                                                <span className="text-xs text-gray-400">({f.example})</span>
                                              </div>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                {/* Description field for KPI */}
                                <div>
                                  <Label className={colors.textSecondary}>Description (optional)</Label>
                                  <Textarea
                                    value={kpi.description || ''}
                                    onChange={(e) => handleKPIChange(kpi.kpi_id, 'description', e.target.value)}
                                    placeholder="Add context about this KPI, how it's measured, or why it matters..."
                                    rows={2}
                                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                                    data-testid={`kpi-description-${kpi.kpi_id}`}
                                  />
                                </div>
                                
                                {/* Direction and Interval (new Outcome-Driven fields) */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Direction {isLocked && "🔒"}
                                    </Label>
                                    <Select 
                                      value={kpi.direction || 'higher_is_better'} 
                                      onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'direction', v)}
                                      disabled={isLocked}
                                    >
                                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                                        {DIRECTION_OPTIONS.map(d => {
                                          const Icon = d.icon;
                                          return (
                                            <SelectItem key={d.value} value={d.value}>
                                              <div className="flex items-center gap-2">
                                                <Icon className="w-4 h-4" />
                                                <span>{d.label}</span>
                                              </div>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Measurement Interval {isLocked && "🔒"}
                                    </Label>
                                    <Select 
                                      value={kpi.measurement_interval || 'monthly'} 
                                      onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'measurement_interval', v)}
                                      disabled={isLocked}
                                    >
                                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                                        {INTERVAL_OPTIONS.map(i => (
                                          <SelectItem key={i.value} value={i.value}>
                                            {i.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Start Value {isLocked && "🔒"}
                                    </Label>
                                    <Input
                                      type="number"
                                      value={kpi.start_value || 0}
                                      onChange={(e) => handleKPIChange(kpi.kpi_id, 'start_value', parseFloat(e.target.value) || 0)}
                                      disabled={isLocked}
                                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                                    />
                                  </div>
                                  <div>
                                    <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                                      Target Value {isLocked && "🔒"}
                                    </Label>
                                    <Input
                                      type="number"
                                      value={kpi.target_value || 0}
                                      onChange={(e) => handleKPIChange(kpi.kpi_id, 'target_value', parseFloat(e.target.value) || 0)}
                                      disabled={isLocked}
                                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Initiatives Tab */}
              {activeTab === 'initiatives' && (
                <div className="space-y-4">
                  {/* Execution Progress - Summary at top */}
                  {mode === 'edit' && computedMetrics && (
                    <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn("text-sm font-medium", colors.textPrimary)}>Execution Progress</span>
                        <span className={cn("text-sm", colors.textSecondary)}>{computedMetrics.execution_progress || 0}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${computedMetrics.execution_progress || 0}%` }}
                        />
                      </div>
                      {computedMetrics.blocked_count > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-amber-600 dark:text-amber-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs">{computedMetrics.blocked_count} initiative(s) blocked</span>
                        </div>
                      )}
                      {/* Investment Allocation */}
                      <div className={cn("mt-3 pt-3 border-t", colors.borderPrimary)}>
                        <span className={cn("text-sm", colors.textSecondary)}>Investment Allocation: </span>
                        <span className={cn("text-sm font-medium", colors.textPrimary)}>
                          {computedMetrics.feature_count} features ({computedMetrics.investment_pct}% of portfolio)
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Add Initiative Search - Inline pattern */}
                  {canEdit && mode === 'edit' && (
                    <div className="relative" ref={initiativeDropdownRef}>
                      <Label className={cn("mb-1 block", colors.textSecondary)}>
                        Link Initiative
                      </Label>
                      <div className="relative">
                        <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                        <Input
                          type="text"
                          placeholder="Search existing initiatives or create new..."
                          value={initiativeSearch}
                          onChange={(e) => {
                            setInitiativeSearch(e.target.value);
                            setShowInitiativeDropdown(true);
                          }}
                          onFocus={() => setShowInitiativeDropdown(true)}
                          className={cn("pl-10", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                          data-testid="initiative-search-input"
                        />
                      </div>
                      
                      {/* Initiative Dropdown */}
                      {showInitiativeDropdown && (
                        <div className={cn(
                          "absolute z-50 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg",
                          colors.bgCard, colors.borderPrimary
                        )}>
                          {/* Inline Create Form */}
                          {showInlineCreate ? (
                            <div className={cn("p-3 border-b", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="text"
                                  placeholder="Initiative title..."
                                  value={newInitiativeTitle}
                                  onChange={(e) => setNewInitiativeTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newInitiativeTitle.trim()) {
                                      handleInlineCreateInitiative();
                                    } else if (e.key === 'Escape') {
                                      setShowInlineCreate(false);
                                      setNewInitiativeTitle('');
                                    }
                                  }}
                                  autoFocus
                                  disabled={creatingInitiative}
                                  className={cn("flex-1 h-8 text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                                  data-testid="new-initiative-title-input"
                                />
                                <Button
                                  size="sm"
                                  onClick={handleInlineCreateInitiative}
                                  disabled={!newInitiativeTitle.trim() || creatingInitiative}
                                  className="h-8 px-3"
                                >
                                  {creatingInitiative ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Plus className="w-3 h-3" />
                                  )}
                                </Button>
                                <button
                                  onClick={() => {
                                    setShowInlineCreate(false);
                                    setNewInitiativeTitle('');
                                  }}
                                  className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <p className={cn("text-xs mt-1", colors.textMuted)}>
                                Press Enter to create, Esc to cancel
                              </p>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowInlineCreate(true)}
                              className={cn(
                                "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b",
                                colors.borderPrimary, "text-indigo-600 dark:text-indigo-400"
                              )}
                            >
                              <Plus className="w-4 h-4" />
                              <span className="font-medium">Create New Initiative</span>
                            </button>
                          )}
                          
                          {/* Existing Initiatives */}
                          {allProductInitiatives
                            .filter(init => 
                              !linkedInitiatives.some(li => li.initiative_id === init.initiative_id) &&
                              (!initiativeSearch || init.title?.toLowerCase().includes(initiativeSearch.toLowerCase()))
                            )
                            .map(init => {
                              const statusConfig = STATUS_OPTIONS.find(s => s.value === init.status) || STATUS_OPTIONS[0];
                              return (
                                <button
                                  key={init.initiative_id}
                                  onClick={() => handleLinkInitiative(init)}
                                  className={cn(
                                    "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                                    colors.textPrimary
                                  )}
                                >
                                  <Rocket className="w-4 h-4 text-indigo-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{init.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className={cn("text-xs px-1.5 py-0.5 rounded", statusConfig.color, "text-white")}>
                                        {statusConfig.label}
                                      </span>
                                    </div>
                                  </div>
                                  <Plus className={cn("w-4 h-4", colors.textMuted)} />
                                </button>
                              );
                            })
                          }
                          {allProductInitiatives.filter(init => 
                            !linkedInitiatives.some(li => li.initiative_id === init.initiative_id)
                          ).length === 0 && !showInlineCreate && (
                            <div className={cn("p-4 text-center", colors.textMuted)}>
                              <p className="text-sm">All initiatives are already linked</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {loadingInitiatives ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
                    </div>
                  ) : linkedInitiatives.length === 0 ? (
                    <div className={cn("text-center py-8 rounded-lg border", colors.borderPrimary)}>
                      <Rocket className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                      <p className={cn("text-sm", colors.textMuted)}>No initiatives linked yet</p>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        Use the search above to link existing initiatives or create a new one
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {linkedInitiatives.map(init => {
                        const statusConfig = STATUS_OPTIONS.find(s => s.value === init.status) || STATUS_OPTIONS[0];
                        
                        return (
                          <div 
                            key={init.initiative_id}
                            className={cn("p-3 rounded-lg border flex items-center justify-between", colors.borderPrimary, colors.bgTertiary)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", "bg-indigo-100 dark:bg-indigo-900/30")}>
                                <Rocket className="w-4 h-4 text-indigo-500" />
                              </div>
                              <div>
                                <p className={cn("font-medium", colors.textPrimary)}>{init.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={cn("text-xs px-2 py-0.5 rounded-full", statusConfig.color, "text-white")}>
                                    {statusConfig.label}
                                  </span>
                                  {init.computed_progress !== undefined && (
                                    <span className={cn("text-xs", colors.textMuted)}>
                                      {init.computed_progress}% complete
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {onOpenInitiative && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => onOpenInitiative(init)}
                                  title="Open initiative"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                              {canEdit && mode === 'edit' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUnlinkInitiativeClick(init.initiative_id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  title="Unlink initiative"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Attachments Tab */}
              {activeTab === 'attachments' && goal?.goal_id && (
                <AttachmentsPanel
                  entityType="goal"
                  entityId={goal.goal_id}
                  productId={productContext?.product_id}
                  canEdit={true}
                  colors={colors}
                />
              )}
              
              {/* Notes Tab */}
              {activeTab === 'notes' && goal?.goal_id && (
                <NotesPanel
                  entityType="goal"
                  entityId={goal.goal_id}
                  teamMembers={teamMembers}
                  colors={colors}
                />
              )}
              
              {/* Stability Log Tab */}
              {activeTab === 'stability' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className={cn("text-sm font-semibold", colors.textPrimary)}>
                      Stability Log
                    </h3>
                    {goal?.is_shifted && (
                      <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                        ⚠️ Goal Shifted
                      </span>
                    )}
                  </div>
                  
                  <p className={cn("text-sm", colors.textMuted)}>
                    Track status changes and KPI modifications over time.
                  </p>
                  
                  {(!goal?.stability_log || goal.stability_log.length === 0) ? (
                    <div className={cn("text-center py-8 rounded-lg border border-dashed", colors.borderPrimary)}>
                      <p className={cn("text-sm", colors.textMuted)}>No stability events logged yet</p>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        Events are logged when goal status changes or KPIs are modified after activation.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[...(goal.stability_log || [])].reverse().map((entry, idx) => {
                        const isInstabilityTriggered = entry.instability_triggered;
                        
                        // Get all changes from the entry
                        const changes = entry.changes || [];
                        const statusChange = changes.find(c => c.type === 'status_change');
                        const otherChanges = changes.filter(c => c.type !== 'status_change');
                        
                        // Legacy format support (old entries with entry.details)
                        const legacyDetails = !entry.changes && entry.details;
                        
                        return (
                          <div 
                            key={entry.log_id || idx}
                            className={cn(
                              "p-3 rounded-lg border",
                              isInstabilityTriggered ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20" : colors.borderPrimary,
                              colors.bgTertiary
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                {/* Header: Badge + Timestamp */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={cn(
                                    "text-xs px-2 py-0.5 rounded font-medium",
                                    isInstabilityTriggered ? "bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200" :
                                    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  )}>
                                    {isInstabilityTriggered ? '⚠️ Instability Triggered' : 'Status Change'}
                                  </span>
                                  <span className={cn("text-xs", colors.textMuted)}>
                                    {formatDateTime(entry.timestamp)}
                                  </span>
                                </div>
                                
                                {/* User name */}
                                <p className={cn("text-sm mt-1 font-medium", colors.textPrimary)}>
                                  {entry.user_name}
                                </p>
                                
                                {/* Status Change (primary) */}
                                {statusChange && (
                                  <div className={cn("text-xs mt-1", colors.textMuted)}>
                                    Changed status: <strong>{statusChange.old_value}</strong> → <strong>{statusChange.new_value}</strong>
                                  </div>
                                )}
                                
                                {/* Other changes from baseline (KPIs, fields, etc.) */}
                                {otherChanges.length > 0 && (
                                  <div className={cn("text-xs mt-2 space-y-1 pl-3 border-l-2 border-blue-300 dark:border-blue-700")}>
                                    {otherChanges.map((change, changeIdx) => (
                                      <div key={changeIdx} className={colors.textMuted}>
                                        {change.field === 'kpi_added' && (
                                          <span>Added KPI: <strong>{change.kpi_name}</strong></span>
                                        )}
                                        {change.field === 'kpi_removed' && (
                                          <span>Removed KPI: <strong>{change.kpi_name}</strong></span>
                                        )}
                                        {change.field?.startsWith('kpi_') && !['kpi_added', 'kpi_removed'].includes(change.field) && (
                                          <span>
                                            {change.kpi_name}: {change.field.replace('kpi_', '').replace(/_/g, ' ')} 
                                            {change.old_value !== undefined && <> from <strong>{String(change.old_value)}</strong></>}
                                            {change.new_value !== undefined && <> to <strong>{String(change.new_value)}</strong></>}
                                          </span>
                                        )}
                                        {change.field === 'title' && (
                                          <span>Title changed to: <strong>{change.new_value}</strong></span>
                                        )}
                                        {change.field === 'description' && (
                                          <span>Description updated</span>
                                        )}
                                        {change.field === 'owner_id' && (
                                          <span>Owner changed</span>
                                        )}
                                        {!change.field?.startsWith('kpi_') && !['title', 'description', 'owner_id', 'status'].includes(change.field) && change.field && (
                                          <span>{change.field} changed</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Legacy format support */}
                                {legacyDetails && (
                                  <div className={cn("text-xs mt-1", colors.textMuted)}>
                                    {legacyDetails.field === 'status' && (
                                      <span>Changed status: <strong>{legacyDetails.old_value}</strong> → <strong>{legacyDetails.new_value}</strong></span>
                                    )}
                                    {legacyDetails.field?.startsWith('kpi_') && (
                                      <span>
                                        {legacyDetails.kpi_name}: {legacyDetails.field.replace('kpi_', '')} changed
                                        {legacyDetails.old_value !== undefined && <> from <strong>{String(legacyDetails.old_value)}</strong></>}
                                        {legacyDetails.new_value !== undefined && <> to <strong>{String(legacyDetails.new_value)}</strong></>}
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {/* Instability reason */}
                                {entry.instability_reason && (
                                  <p className={cn("mt-2 text-xs", colors.textMuted)}>
                                    Reason: {entry.instability_reason}
                                  </p>
                                )}
                                
                                {/* Reactivation Info - shows when goal was reactivated after instability */}
                                {entry.reactivated_at && (
                                  <div className={cn("mt-3 pt-3 border-t", colors.borderPrimary)}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs px-2 py-0.5 rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                        ✓ Reactivated
                                      </span>
                                      <span className={cn("text-xs", colors.textMuted)}>
                                        {formatDateTime(entry.reactivated_at)}
                                      </span>
                                    </div>
                                    
                                    <p className={cn("text-sm mt-1 font-medium", colors.textPrimary)}>
                                      {entry.reactivated_by_name}
                                    </p>
                                    
                                    {/* Reactivation reason summary */}
                                    {entry.reactivation_reason && (
                                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                                        {entry.reactivation_reason}
                                      </p>
                                    )}
                                    
                                    {/* Reactivation changelog - what changed during draft period */}
                                    {entry.reactivation_changelog && entry.reactivation_changelog.length > 0 && (
                                      <div className={cn("text-xs mt-2 space-y-1 pl-3 border-l-2 border-green-300 dark:border-green-700")}>
                                        {entry.reactivation_changelog.map((change, changeIdx) => (
                                          <div key={changeIdx} className={colors.textMuted}>
                                            {change.field === 'kpi_added' && (
                                              <span>Added KPI: <strong>{change.kpi_name || change.new_value}</strong></span>
                                            )}
                                            {change.field === 'kpi_removed' && (
                                              <span>Removed KPI: <strong>{change.kpi_name || change.old_value}</strong></span>
                                            )}
                                            {change.field === 'kpi_modified' && (
                                              <span>Modified KPI: <strong>{change.kpi_name}</strong></span>
                                            )}
                                            {change.field === 'title' && (
                                              <span>Title: <strong>{change.old_value}</strong> → <strong>{change.new_value}</strong></span>
                                            )}
                                            {change.field === 'description' && (
                                              <span>Description updated</span>
                                            )}
                                            {change.field === 'target_date' && (
                                              <span>Target date changed</span>
                                            )}
                                            {change.field === 'owner_id' && (
                                              <span>Owner changed</span>
                                            )}
                                            {!['kpi_added', 'kpi_removed', 'kpi_modified', 'title', 'description', 'target_date', 'owner_id'].includes(change.field) && change.field && (
                                              <span>{change.field.replace(/_/g, ' ')} changed</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Right: AI Co-Pilot (40%) - Only shown for existing goals */}
          {showAICopilot && (
            <div className={cn("w-[40%] flex flex-col", colors.bgTertiary)}>
              <UnifiedCoPilot
                contextType="goal"
                contextId={goal?.goal_id}
                productId={productContext?.product_id}
                contextData={formData}
                relatedData={{
                  product: productContext,
                  children: linkedInitiatives,
                  computedMetrics: computedMetrics,
                  teamMembers: teamMembers
                }}
                onApplyUpdates={handleApplyUpdates}
                canEditField={() => true}
                onRefresh={refreshComputedData}
                variant="item"
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={formData.title}
        isDeleting={deleting}
      />
      
      {/* Add Initiative Modal */}
      <AddInitiativeModal
        isOpen={showAddInitiativeModal}
        onClose={() => setShowAddInitiativeModal(false)}
        onSave={() => {
          loadLinkedInitiatives(goal.goal_id);
          // Refresh computed metrics
          goalsApi.get(goal.goal_id).then(res => {
            setComputedMetrics(res.data?.computed_metrics || null);
          }).catch(() => {});
        }}
        goalId={goal?.goal_id}
        productId={productContext.product_id}
        existingInitiativeIds={linkedInitiatives.map(i => i.initiative_id)}
      />
      
      {/* KPI Status Settings Modal */}
      <KPISettingsModal
        isOpen={showKpiSettingsModal}
        onClose={() => setShowKpiSettingsModal(false)}
        goalId={goal?.goal_id}
        currentSettings={paceData?.settings}
        onSave={(response) => {
          // Update pace data with new calculations
          if (response.pace) {
            setPaceData(response.pace);
          }
          // Refresh computed metrics only - preserve unsaved form edits
          refreshComputedData();
        }}
      />
      
      {/* Activation Validation Modal */}
      <ActivationValidationModal
        isOpen={showActivationValidation}
        onClose={() => setShowActivationValidation(false)}
        validationErrors={activationErrors}
      />
      
      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUnsavedWarning(false)} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
            <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Unsaved Changes</h3>
            <p className={cn("text-sm mb-4", colors.textSecondary)}>
              You have unsaved changes. Do you want to save them before closing?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUnsavedWarning(false)}>
                Cancel
              </Button>
              <Button variant="ghost" onClick={performClose}>
                Discard
              </Button>
              {canSaveGoal && (
                <Button onClick={handleSaveAndClose} className={cn(colors.accentGradient, "text-white")}>
                  Save & Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Stability Warning Modal */}
      {showStabilityWarning && stabilityCheckData && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => {
            setShowStabilityWarning(false);
            setStabilityCheckData(null);
            setPendingStatusChange(null);
          }} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Stability Impact Warning</h3>
                <p className={cn("text-sm", colors.textMuted)}>This action will affect your team's stability score</p>
              </div>
            </div>
            
            <div className={cn("p-4 rounded-lg mb-4 space-y-3", colors.bgTertiary)}>
              <p className={cn("text-sm", colors.textSecondary)}>
                Moving <strong className={colors.textPrimary}>"{formData.title}"</strong> back to Draft status 
                outside the grace period will mark this goal as <strong className="text-amber-600 dark:text-amber-400">unstable</strong>.
              </p>
              
              <div className={cn("text-sm space-y-1", colors.textMuted)}>
                <div className="flex justify-between">
                  <span>Grace period:</span>
                  <span className={colors.textPrimary}>{stabilityCheckData.grace_period_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span>Last activated:</span>
                  <span className={colors.textPrimary}>
                    {stabilityCheckData.last_activated_at 
                      ? new Date(stabilityCheckData.last_activated_at).toLocaleDateString() 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Grace expired:</span>
                  <span className="text-red-500">
                    {stabilityCheckData.grace_expires_at 
                      ? new Date(stabilityCheckData.grace_expires_at).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
              
              <p className={cn("text-xs pt-2 border-t", colors.borderPrimary, colors.textMuted)}>
                This will impact your Goal Stability score on the dashboard for the next 6 months.
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowStabilityWarning(false);
                  setStabilityCheckData(null);
                  setPendingStatusChange(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  // Proceed with the status change
                  if (pendingStatusChange) {
                    setFormData(prev => ({ ...prev, status: pendingStatusChange }));
                  }
                  setShowStabilityWarning(false);
                  setStabilityCheckData(null);
                  setPendingStatusChange(null);
                }}
              >
                Move to Draft Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Cascade Unlink Warning Modal */}
      {cascadeModalOpen && cascadeImpact && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className={cn("absolute inset-0 bg-black/60")} onClick={cancelCascadeUnlink} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Unlink Initiative</h3>
                <p className={cn("text-sm", colors.textMuted)}>This action will remove all related data</p>
              </div>
            </div>
            
            <div className={cn("p-4 rounded-lg mb-4", colors.bgTertiary)}>
              <p className={cn("text-sm mb-3", colors.textSecondary)}>
                Unlinking <strong className={colors.textPrimary}>{cascadeImpact.initiative_title}</strong> from{' '}
                <strong className={colors.textPrimary}>{cascadeImpact.goal_title}</strong> will remove:
              </p>
              
              <ul className="space-y-2">
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    Subscribed KPIs
                  </span>
                  <span className={cn("font-medium", cascadeImpact.impact.subscribed_kpis?.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {cascadeImpact.impact.subscribed_kpis?.count || 0}
                  </span>
                </li>
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-purple-500" />
                    Epic KPI Subscriptions
                  </span>
                  <span className={cn("font-medium", cascadeImpact.impact.epics.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {cascadeImpact.impact.epics.count}
                  </span>
                </li>
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-500" />
                    Feature Alignments
                  </span>
                  <span className={cn("font-medium", cascadeImpact.impact.features.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {cascadeImpact.impact.features.count}
                  </span>
                </li>
              </ul>
            </div>
            
            {cascadeImpact.has_impact && (
              <div className={cn("p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 mb-4")}>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Warning:</strong> This will permanently delete metric alignment data. 
                  Epics and Features will remain but lose their strategic linkage to this Goal.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={cancelCascadeUnlink} disabled={cascadeLoading}>
                Cancel
              </Button>
              <Button 
                onClick={executeUnlinkInitiative} 
                disabled={cascadeLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {cascadeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Unlink & Remove Data
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* KPI Cascade Delete Warning Modal */}
      {kpiCascadeModalOpen && kpiCascadeImpact && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className={cn("absolute inset-0 bg-black/60")} onClick={cancelKpiCascadeDelete} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Delete KPI</h3>
                <p className={cn("text-sm", colors.textMuted)}>This will be applied when you save the goal</p>
              </div>
            </div>
            
            <div className={cn("p-4 rounded-lg mb-4", colors.bgTertiary)}>
              <p className={cn("text-sm mb-3", colors.textSecondary)}>
                Deleting <strong className={colors.textPrimary}>{kpiCascadeImpact.kpi_name}</strong> will remove:
              </p>
              
              <ul className="space-y-2">
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-indigo-500" />
                    Initiative Metric Targets
                  </span>
                  <span className={cn("font-medium", kpiCascadeImpact.impact.initiatives.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {kpiCascadeImpact.impact.initiatives.count}
                  </span>
                </li>
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-purple-500" />
                    Epic Target Selections
                  </span>
                  <span className={cn("font-medium", kpiCascadeImpact.impact.epics.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {kpiCascadeImpact.impact.epics.count}
                  </span>
                </li>
                <li className={cn("flex items-center justify-between text-sm", colors.textPrimary)}>
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-500" />
                    Feature Alignments
                  </span>
                  <span className={cn("font-medium", kpiCascadeImpact.impact.features.count > 0 ? "text-red-500" : colors.textMuted)}>
                    {kpiCascadeImpact.impact.features.count}
                  </span>
                </li>
              </ul>
            </div>
            
            {kpiCascadeImpact.has_impact && (
              <div className={cn("p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 mb-4")}>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Warning:</strong> When you save, this will permanently delete this KPI and all strategic alignments 
                  referencing it across Initiatives, Epics, and Features.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={cancelKpiCascadeDelete} disabled={kpiCascadeLoading}>
                Cancel
              </Button>
              <Button 
                onClick={executeKpiCascadeDelete} 
                disabled={kpiCascadeLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove KPI
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Instability Reason Modal */}
      {instabilityReasonModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className={cn("absolute inset-0 bg-black/60")} onClick={() => setInstabilityReasonModal({ open: false, reason: '', pendingAction: null })} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Goal Instability Warning</h3>
                <p className={cn("text-sm", colors.textMuted)}>This action will mark the goal as unstable</p>
              </div>
            </div>
            
            <div className={cn("p-4 rounded-lg mb-4 border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20")}>
              <p className={cn("text-sm text-amber-800 dark:text-amber-300")}>
                Moving this goal to Draft outside the grace period will mark it as <strong>unstable</strong>. 
                This indicates a strategic shift and will be tracked in the goal's stability log.
              </p>
            </div>
            
            <div className="mb-4">
              <label className={cn("block text-sm font-medium mb-2", colors.textPrimary)}>
                Why are you moving this goal to Draft? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={instabilityReasonModal.reason}
                onChange={(e) => setInstabilityReasonModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Need to revise KPIs based on Q3 results, Pivoting strategy due to market changes..."
                className={cn(
                  "w-full px-3 py-2 rounded-lg border text-sm min-h-[100px] resize-none",
                  colors.bgTertiary, colors.borderPrimary, colors.textPrimary,
                  "placeholder:text-gray-400 dark:placeholder:text-gray-500",
                  "focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                )}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setInstabilityReasonModal({ open: false, reason: '', pendingAction: null })}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleInstabilityReasonConfirm}
                disabled={!instabilityReasonModal.reason.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm & Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModalZIndexProvider>
  );
};

export default GoalDetailModal;
